import React, { useState, useEffect } from 'react';
import { CreditCard, Search, Edit, Calendar, DollarSign, CheckCircle, XCircle, AlertCircle, Users, Save, Trash2, RefreshCw, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const SubscriptionManagement = ({ token, i18n }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchSubscribers();
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription-plans/public`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/users?limit=10000`, { headers });
      const users = res.data.users || [];
      setSubscribers(users);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const [sendingReminderUserId, setSendingReminderUserId] = useState(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderUser, setReminderUser] = useState(null);
  const [reminderFields, setReminderFields] = useState({
    subject: '', headline: '', hello: '', intro: '',
    what_you_lose: '', features: ['', '', '', ''],
    cta: '', secondary: '', footer: '',
    language: 'sv', days_remaining: 3, expiry_date: '',
  });
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const openReminderModal = async (user, lang = 'sv') => {
    if (!user?.id) return;
    setReminderUser(user);
    setReminderModalOpen(true);
    setLoadingTemplate(true);
    setPreviewHtml('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/users/${user.id}/reminder-template?language=${lang}`, { headers });
      const d = res.data;
      setReminderFields({
        subject: d.fields?.subject || '',
        headline: d.fields?.headline || '',
        hello: d.fields?.hello || '',
        intro: d.fields?.intro || '',
        what_you_lose: d.fields?.what_you_lose || '',
        features: d.fields?.features || ['', '', '', ''],
        cta: d.fields?.cta || '',
        secondary: d.fields?.secondary || '',
        footer: d.fields?.footer || '',
        language: d.language || 'sv',
        days_remaining: d.days_remaining || 3,
        expiry_date: d.expiry_date || '',
      });
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error(i18n.language === 'ar' ? 'فشل تحميل القالب' : 'Failed to load template');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleReminderLanguageChange = async (newLang) => {
    if (!reminderUser?.id) return;
    setLoadingTemplate(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/users/${reminderUser.id}/reminder-template?language=${newLang}`, { headers });
      const d = res.data;
      setReminderFields({
        subject: d.fields?.subject || '',
        headline: d.fields?.headline || '',
        hello: d.fields?.hello || '',
        intro: d.fields?.intro || '',
        what_you_lose: d.fields?.what_you_lose || '',
        features: d.fields?.features || ['', '', '', ''],
        cta: d.fields?.cta || '',
        secondary: d.fields?.secondary || '',
        footer: d.fields?.footer || '',
        language: newLang,
        days_remaining: d.days_remaining || 3,
        expiry_date: d.expiry_date || '',
      });
      setPreviewHtml('');
    } catch (error) {
      console.error('Error switching language:', error);
      toast.error(i18n.language === 'ar' ? 'فشل تغيير اللغة' : 'Language switch failed');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handlePreviewReminder = async () => {
    setPreviewLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/preview-custom-reminder`, {
        ...reminderFields,
        name: reminderUser?.name || reminderUser?.email?.split('@')[0] || 'User',
      }, { headers, responseType: 'text' });
      setPreviewHtml(res.data);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(i18n.language === 'ar' ? 'فشل المعاينة' : 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendCustomReminder = async () => {
    if (!reminderUser?.id) return;
    setSendingReminderUserId(reminderUser.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/admin/users/${reminderUser.id}/send-custom-reminder`,
        reminderFields,
        { headers }
      );
      if (res.data?.sent) {
        toast.success(
          i18n.language === 'ar'
            ? `تم إرسال التذكير ✅ (${(res.data.language || '').toUpperCase()})`
            : `Reminder sent ✅ (${(res.data.language || '').toUpperCase()})`
        );
        setReminderModalOpen(false);
      } else {
        toast.error(
          i18n.language === 'ar'
            ? 'فشل إرسال الإيميل (تحقق من نطاق Resend في الإنتاج)'
            : 'Email send failed (check Resend domain in production)'
        );
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الإرسال' : 'Error sending');
    } finally {
      setSendingReminderUserId(null);
    }
  };

  // ---- Free-form custom message ----
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageUser, setMessageUser] = useState(null);
  const [messageFields, setMessageFields] = useState({
    subject: '', body: '', cta_text: '', cta_url: '', language: 'sv',
  });
  const [messagePreviewHtml, setMessagePreviewHtml] = useState('');
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);
  const [sendingMessageUserId, setSendingMessageUserId] = useState(null);

  const openMessageModal = (user) => {
    if (!user?.id) return;
    setMessageUser(user);
    setMessageFields({ subject: '', body: '', cta_text: '', cta_url: '', language: 'sv' });
    setMessagePreviewHtml('');
    setMessageModalOpen(true);
  };

  const handlePreviewMessage = async () => {
    setMessagePreviewLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/preview-custom-message`, {
        ...messageFields,
      }, { headers, responseType: 'text' });
      setMessagePreviewHtml(res.data);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(i18n.language === 'ar' ? 'فشل المعاينة' : 'Preview failed');
    } finally {
      setMessagePreviewLoading(false);
    }
  };

  const handleSendCustomMessage = async () => {
    if (!messageUser?.id) return;
    if (!messageFields.subject.trim() || !messageFields.body.trim()) {
      toast.error(i18n.language === 'ar' ? 'العنوان والنص مطلوبان' : 'Subject and body are required');
      return;
    }
    setSendingMessageUserId(messageUser.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/admin/users/${messageUser.id}/send-custom-message`,
        messageFields,
        { headers }
      );
      if (res.data?.sent) {
        toast.success(i18n.language === 'ar' ? 'تم إرسال الرسالة ✅' : 'Message sent ✅');
        setMessageModalOpen(false);
      } else {
        toast.error(i18n.language === 'ar' ? 'فشل الإرسال (تحقق من Resend)' : 'Send failed (check Resend)');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الإرسال' : 'Error sending');
    } finally {
      setSendingMessageUserId(null);
    }
  };




  const handleEditSubscription = (user) => {
    // Get the best available end date from any field
    const endDate = user.subscription_end || user.subscription_end_date || user.subscription_expiry || '';
    setEditingUser({
      ...user,
      newSubscriptionPlan: user.subscription_plan || 'free',
      newSubscriptionStatus: user.subscription_status || 'active',
      newSubscriptionEnd: endDate ? endDate.split('T')[0] : '',
      originalPlan: user.subscription_plan || 'free'
    });
    setModalOpen(true);
  };

  const handleUpdateSubscription = async () => {
    if (!editingUser) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      let endDate = editingUser.newSubscriptionEnd;
      const planChanged = editingUser.newSubscriptionPlan !== editingUser.originalPlan;
      
      // Lifetime subscription doesn't have an end date
      if (editingUser.newSubscriptionPlan === 'lifetime') {
        endDate = null;
      } else if (editingUser.newSubscriptionPlan === 'free') {
        endDate = null;
      } else if (editingUser.newSubscriptionPlan === 'trial_hour') {
        // 60-minute paid trial — backend will overwrite this exact minute
        // anyway, but we send an ISO datetime so the field is consistent.
        endDate = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      } else if (planChanged || !endDate) {
        // إذا تغيرت الخطة أو لم يوجد تاريخ → حساب تاريخ جديد من اليوم
        const now = new Date();
        if (editingUser.newSubscriptionPlan === 'monthly') {
          now.setMonth(now.getMonth() + 1);
        } else if (editingUser.newSubscriptionPlan === 'six_months') {
          now.setMonth(now.getMonth() + 6);
        } else if (editingUser.newSubscriptionPlan === 'yearly' || editingUser.newSubscriptionPlan === 'annual') {
          now.setFullYear(now.getFullYear() + 1);
        }
        endDate = now.toISOString().split('T')[0];
      }
      
      const payload = {
        subscription_plan: editingUser.newSubscriptionPlan,
        subscription_status: (editingUser.newSubscriptionPlan === 'free') ? 'expired' 
          : (editingUser.newSubscriptionPlan === 'lifetime') ? 'active'
          : (editingUser.newSubscriptionPlan === 'trial_hour') ? 'active'
          : editingUser.newSubscriptionStatus,
        subscription_end: endDate
      };
      
      await axios.put(
        `${API}/admin/users/${editingUser.id}`,
        payload,
        { headers }
      );
      
      toast.success(i18n.language === 'ar' ? 'تم تحديث الاشتراك! ✅' : 'Subscription updated! ✅');
      fetchSubscribers();
      setModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في التحديث' : 'Error updating');
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedUser) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(
        `${API}/admin/users/${selectedUser.id}`,
        {
          subscription_plan: 'free',
          subscription_status: 'expired',
          subscription_end: null
        },
        { headers }
      );
      
      toast.success(i18n.language === 'ar' ? 'تم إلغاء الاشتراك! ✅' : i18n.language === 'sv' ? 'Prenumeration avbruten! ✅' : 'Subscription cancelled! ✅');
      fetchSubscribers();
      setCancelModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الإلغاء' : i18n.language === 'sv' ? 'Avbokningsfel' : 'Error cancelling');
    }
  };

  const handleRefund = async () => {
    if (!selectedUser) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Process refund via Stripe API
      const response = await axios.post(
        `${API}/admin/refund/${selectedUser.id}`,
        {},
        { headers }
      );
      
      if (response.data.success) {
        toast.success(
          i18n.language === 'ar' 
            ? `تم الاسترداد بنجاح! المبلغ: ${response.data.amount_refunded} ${response.data.currency.toUpperCase()} ✅` 
            : i18n.language === 'sv' 
            ? `Återbetalning genomförd! Belopp: ${response.data.amount_refunded} ${response.data.currency.toUpperCase()} ✅`
            : `Refund processed! Amount: ${response.data.amount_refunded} ${response.data.currency.toUpperCase()} ✅`
        );
      }
      
      fetchSubscribers();
      setRefundModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error processing refund:', error);
      const errorMessage = error.response?.data?.detail || 'Error processing refund';
      toast.error(
        i18n.language === 'ar' 
          ? `خطأ في الاسترداد: ${errorMessage}` 
          : i18n.language === 'sv' 
          ? `Återbetalningsfel: ${errorMessage}`
          : `Refund error: ${errorMessage}`
      );
    }
  };

  const getPlanName = (planId) => {
    // Map plan IDs to display names
    const planNames = {
      'free': { en: 'Free', ar: 'مجاني', sv: 'Gratis' },
      'monthly': { en: 'Monthly', ar: 'شهري', sv: 'Månad' },
      'six_months': { en: '6 Months', ar: '6 أشهر', sv: '6 månader' },
      'yearly': { en: 'Yearly', ar: 'سنوي', sv: 'Årlig' },
      'annual': { en: 'Annual', ar: 'سنوي', sv: 'Årlig' },
      'lifetime': { en: 'Lifetime', ar: 'دائم', sv: 'Livstid' },
      'trial_hour': { en: '1-Hour Trial', ar: 'تجربة ساعة', sv: '1 timme prov' }
    };
    
    const lang = i18n.language === 'ar' ? 'ar' : i18n.language === 'sv' ? 'sv' : 'en';
    return planNames[planId]?.[lang] || planId;
  };

  const getStatusBadge = (status, planType) => {
    // Check if subscription is expired
    const isExpired = status === 'expired';
    
    if (isExpired) {
      return <Badge className="bg-red-100 text-red-700">⏰ {i18n.language === 'ar' ? 'منتهي' : 'Expired'}</Badge>;
    }
    
    // Show plan type badges
    switch(planType) {
      case 'monthly':
        return <Badge className="bg-emerald-100 text-emerald-700">💎 {getPlanName('monthly')}</Badge>;
      case 'six_months':
        return <Badge className="bg-purple-100 text-purple-700">⭐ {getPlanName('six_months')}</Badge>;
      case 'trial_hour':
        return <Badge className="bg-cyan-100 text-cyan-700">⏱️ {getPlanName('trial_hour')}</Badge>;
      case 'yearly':
      case 'annual':
        return <Badge className="bg-orange-100 text-orange-700">🏆 {getPlanName('yearly')}</Badge>;
      case 'lifetime':
        return <Badge className="bg-amber-100 text-amber-700">👑 {getPlanName('lifetime')}</Badge>;
      case 'free':
      default:
        // If user has active subscription but plan isn't recognized, show as active
        if (status === 'active') {
          return <Badge className="bg-emerald-100 text-emerald-700">✅ {i18n.language === 'ar' ? 'نشط' : 'Active'}</Badge>;
        }
        return <Badge className="bg-slate-100 text-slate-700">👤 {getPlanName('free')}</Badge>;
    }
  };

  const getStatusColor = (planType) => {
    switch(planType) {
      case 'monthly': return 'border-emerald-300 bg-emerald-50';
      case 'six_months': return 'border-purple-300 bg-purple-50';
      case 'yearly':
      case 'annual': return 'border-orange-300 bg-orange-50';
      case 'lifetime': return 'border-amber-300 bg-amber-50';
      case 'free':
      default: return 'border-slate-200 bg-slate-50';
    }
  };

  const filteredSubscribers = subscribers
    .filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesFilter = true;
      if (filterStatus === 'expired') {
        matchesFilter = user.subscription_status === 'expired';
      } else if (filterStatus === 'yearly') {
        matchesFilter = user.subscription_plan === 'yearly' || user.subscription_plan === 'annual';
      } else if (filterStatus !== 'all') {
        matchesFilter = user.subscription_plan === filterStatus;
      }
      
      return matchesSearch && matchesFilter;
    });

  const stats = {
    total: subscribers.length,
    free: subscribers.filter(u => !u.subscription_plan || u.subscription_plan === 'free' || u.subscription_plan === 'demo' || u.is_demo_account).length,
    monthly: subscribers.filter(u => u.subscription_plan === 'monthly' && u.subscription_status === 'active').length,
    six_months: subscribers.filter(u => u.subscription_plan === 'six_months' && u.subscription_status === 'active').length,
    yearly: subscribers.filter(u => (u.subscription_plan === 'yearly' || u.subscription_plan === 'annual') && u.subscription_status === 'active').length,
    lifetime: subscribers.filter(u => u.subscription_plan === 'lifetime' && u.subscription_status === 'active').length,
    expired: subscribers.filter(u => u.subscription_status === 'expired').length
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-7 h-7 text-emerald-600" />
              {i18n.language === 'ar' ? 'إدارة المشتركين' : 'Subscription Management'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {i18n.language === 'ar' 
                ? `${subscribers.length} مستخدم مسجل`
                : `${subscribers.length} registered users`
              }
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 md:gap-4 mb-6">
          {/* Free Plan */}
          <div className="relative bg-gradient-to-br from-slate-50 to-white rounded-none md:rounded-2xl p-6 md:p-4 border-b-2 border-r-2 md:border-2 border-slate-300 md:border-slate-200 overflow-hidden active:scale-95 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-slate-200/30 to-transparent rounded-full -mr-10 -mt-10"></div>
            <div className="relative z-10 flex flex-col items-center text-center md:block md:text-left">
              <div className="w-14 h-14 md:w-auto md:h-auto md:p-2 bg-slate-200 rounded-2xl md:rounded-lg flex items-center justify-center mb-3 md:mb-2">
                <span className="text-2xl md:text-base">👤</span>
              </div>
              <div className="text-3xl md:text-2xl font-black text-slate-700 mb-1">{stats.free}</div>
              <div className="text-sm md:text-sm text-slate-600 font-bold">
                {i18n.language === 'ar' ? 'مجاني' : i18n.language === 'sv' ? 'Gratis' : 'Free'}
              </div>
              <div className="text-xs text-slate-500 mt-1">0 sek</div>
            </div>
          </div>
          
          {/* Monthly Plan */}
          <div className="relative bg-gradient-to-br from-emerald-50 to-white rounded-none md:rounded-2xl p-6 md:p-4 border-b-2 border-r-2 md:border-2 border-emerald-300 md:border-emerald-200 overflow-hidden active:scale-95 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-200/30 to-transparent rounded-full -mr-10 -mt-10"></div>
            <div className="relative z-10 flex flex-col items-center text-center md:block md:text-left">
              <div className="w-14 h-14 md:w-auto md:h-auto md:p-2 bg-emerald-200 rounded-2xl md:rounded-lg flex items-center justify-center mb-3 md:mb-2">
                <span className="text-2xl md:text-base">💎</span>
              </div>
              <div className="text-3xl md:text-2xl font-black text-emerald-700 mb-1">{stats.monthly}</div>
              <div className="text-sm md:text-sm text-emerald-600 font-bold">
                {i18n.language === 'ar' ? 'شهري' : i18n.language === 'sv' ? 'Månadsvis' : 'Monthly'}
              </div>
              <div className="text-xs text-emerald-500 mt-1">249 sek</div>
            </div>
          </div>
          
          {/* Six Months Plan */}
          <div className="relative bg-gradient-to-br from-purple-50 to-white rounded-none md:rounded-2xl p-6 md:p-4 border-b-2 md:border-2 border-purple-300 md:border-purple-200 overflow-hidden active:scale-95 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-200/30 to-transparent rounded-full -mr-10 -mt-10"></div>
            <div className="relative z-10 flex flex-col items-center text-center md:block md:text-left">
              <div className="w-14 h-14 md:w-auto md:h-auto md:p-2 bg-purple-200 rounded-2xl md:rounded-lg flex items-center justify-center mb-3 md:mb-2">
                <span className="text-2xl md:text-base">⭐</span>
              </div>
              <div className="text-3xl md:text-2xl font-black text-purple-700 mb-1">{stats.six_months}</div>
              <div className="text-sm md:text-sm text-purple-600 font-bold">
                {i18n.language === 'ar' ? '6 أشهر' : '6 Months'}
              </div>
              <div className="text-xs text-purple-500 mt-1">699 sek</div>
            </div>
          </div>

          {/* Lifetime Plan */}
          <div className="relative bg-gradient-to-br from-amber-50 to-white rounded-none md:rounded-2xl p-6 md:p-4 border-b-2 border-r-2 md:border-2 border-amber-300 md:border-amber-200 overflow-hidden active:scale-95 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-200/30 to-transparent rounded-full -mr-10 -mt-10"></div>
            <div className="relative z-10 flex flex-col items-center text-center md:block md:text-left">
              <div className="w-14 h-14 md:w-auto md:h-auto md:p-2 bg-amber-200 rounded-2xl md:rounded-lg flex items-center justify-center mb-3 md:mb-2">
                <span className="text-2xl md:text-base">👑</span>
              </div>
              <div className="text-3xl md:text-2xl font-black text-amber-700 mb-1">{stats.lifetime}</div>
              <div className="text-sm md:text-sm text-amber-600 font-bold">
                {i18n.language === 'ar' ? 'مدى الحياة' : i18n.language === 'sv' ? 'Livstid' : 'Lifetime'}
              </div>
              <div className="text-xs text-amber-500 mt-1">∞</div>
            </div>
          </div>

          {/* Yearly Plan */}
          <div className="relative bg-gradient-to-br from-orange-50 to-white rounded-none md:rounded-2xl p-6 md:p-4 border-b-2 border-r-2 md:border-2 border-orange-300 md:border-orange-200 overflow-hidden active:scale-95 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-200/30 to-transparent rounded-full -mr-10 -mt-10"></div>
            <div className="relative z-10 flex flex-col items-center text-center md:block md:text-left">
              <div className="w-14 h-14 md:w-auto md:h-auto md:p-2 bg-orange-200 rounded-2xl md:rounded-lg flex items-center justify-center mb-3 md:mb-2">
                <span className="text-2xl md:text-base">🏆</span>
              </div>
              <div className="text-3xl md:text-2xl font-black text-orange-700 mb-1">{stats.yearly}</div>
              <div className="text-sm md:text-sm text-orange-600 font-bold">
                {i18n.language === 'ar' ? 'سنوي' : i18n.language === 'sv' ? 'Årlig' : 'Yearly'}
              </div>
              <div className="text-xs text-orange-500 mt-1">12 {i18n.language === 'ar' ? 'شهر' : i18n.language === 'sv' ? 'mån' : 'mo'}</div>
            </div>
          </div>
          
          {/* Expired */}
          <div className="relative bg-gradient-to-br from-red-50 to-white rounded-none md:rounded-2xl p-6 md:p-4 border-b-2 md:border-2 border-red-300 md:border-red-200 overflow-hidden active:scale-95 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-200/30 to-transparent rounded-full -mr-10 -mt-10"></div>
            <div className="relative z-10 flex flex-col items-center text-center md:block md:text-left">
              <div className="w-14 h-14 md:w-auto md:h-auto md:p-2 bg-red-200 rounded-2xl md:rounded-lg flex items-center justify-center mb-3 md:mb-2">
                <span className="text-2xl md:text-base">⏰</span>
              </div>
              <div className="text-3xl md:text-2xl font-black text-red-700 mb-1">{stats.expired}</div>
              <div className="text-sm md:text-sm text-red-600 font-bold line-clamp-1">{i18n.language === 'ar' ? 'منتهي' : i18n.language === 'sv' ? 'Utgånget' : 'Expired'}</div>
              <div className="text-xs text-red-500 mt-1 line-clamp-1">{i18n.language === 'ar' ? 'منتهية' : i18n.language === 'sv' ? 'Utgångna' : 'Expired'}</div>
            </div>
          </div>
        </div>

        {/* Filters + Expire Check Button */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder={i18n.language === 'ar' ? 'بحث...' : 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{i18n.language === 'ar' ? 'جميع الخطط' : i18n.language === 'sv' ? 'Alla planer' : 'All Plans'}</SelectItem>
              <SelectItem value="free">{getPlanName('free')}</SelectItem>
              <SelectItem value="monthly">{getPlanName('monthly')}</SelectItem>
              <SelectItem value="six_months">{getPlanName('six_months')}</SelectItem>
              <SelectItem value="yearly">🏆 {getPlanName('yearly')}</SelectItem>
              <SelectItem value="lifetime">👑 {getPlanName('lifetime')}</SelectItem>
              <SelectItem value="expired">{i18n.language === 'ar' ? 'منتهي' : i18n.language === 'sv' ? 'Utgånget' : 'Expired'}</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await axios.post(`${API}/admin/expire-check`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                const data = res.data;
                alert(i18n.language === 'ar' 
                  ? `تم فحص ${data.expired_count || 0} اشتراك منتهي وتحديثه` 
                  : `Expired ${data.expired_count || 0} subscriptions`);
                fetchSubscribers();
              } catch(e) {
                alert(i18n.language === 'ar' ? 'خطأ في الفحص' : 'Error checking');
              }
            }}
          >
            {i18n.language === 'ar' ? '⏰ فحص المنتهية' : '⏰ Check Expired'}
          </Button>
        </div>

        {/* Subscribers Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">
                  {i18n.language === 'ar' ? 'المستخدم' : 'User'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">
                  {i18n.language === 'ar' ? 'نوع الخطة' : 'Plan'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">
                  {i18n.language === 'ar' ? 'تاريخ التسجيل' : 'Joined'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">
                  {i18n.language === 'ar' ? 'تاريخ الانتهاء' : 'Expires'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">
                  {i18n.language === 'ar' ? 'آخر ظهور' : 'Last seen'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">
                  {i18n.language === 'ar' ? 'الدخول' : 'Logins'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">
                  {i18n.language === 'ar' ? 'الإجراءات' : 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {filteredSubscribers.map((user) => {
                const planType = user.subscription_plan || 'free';
                const isExpired = user.subscription_status === 'expired';
                const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'sv' ? 'sv-SE' : 'en-US';

                // 14-day refund window (EU consumer law): show refund button only if
                // less than 14 days have passed since subscription started.
                const planDurationDays = {
                  'monthly': 30,
                  '6_month': 180,
                  '6_months': 180,
                  'six_months': 180,
                  'yearly': 365,
                  'annual': 365,
                }[planType] || null;
                let isWithinRefundWindow = false;
                if (planType !== 'free' && planType !== 'lifetime' && !isExpired) {
                  const startStr = user.subscription_start_date;
                  const endStr = user.subscription_end || user.subscription_end_date || user.subscription_expiry;
                  let startDate = null;
                  if (startStr) {
                    startDate = new Date(startStr);
                  } else if (endStr && planDurationDays) {
                    // Fallback for legacy users without subscription_start_date:
                    // derive start = end - plan_duration
                    startDate = new Date(new Date(endStr).getTime() - planDurationDays * 24 * 60 * 60 * 1000);
                  }
                  if (startDate && !isNaN(startDate.getTime())) {
                    const daysSinceStart = (Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24);
                    isWithinRefundWindow = daysSinceStart <= 14;
                  } else {
                    // If we can't determine — show the button (safer for admin)
                    isWithinRefundWindow = true;
                  }
                }
                
                return (
                  <tr key={user.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800 ${getStatusColor(planType)}`}>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{user.name}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{user.email}</div>
                        {user.auth_provider && (
                          <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                            {user.auth_provider === 'google' ? '🔗 Google' : user.auth_provider}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(user.subscription_status, planType)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {user.created_at 
                        ? new Date(user.created_at).toLocaleDateString(locale)
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {(() => {
                        if (planType === 'lifetime') {
                          return <span className="text-amber-600 font-semibold">👑 {i18n.language === 'ar' ? 'دائم' : 'Lifetime'}</span>;
                        }
                        const endStr = user.subscription_end || user.subscription_end_date || user.subscription_expiry;
                        if (!endStr) {
                          return <span className="text-slate-400">{i18n.language === 'ar' ? 'غير محدد' : 'N/A'}</span>;
                        }
                        const endDate = new Date(endStr);
                        const isPast = endDate < new Date();
                        return (
                          <span className={isPast ? 'text-red-500 font-semibold' : 'text-emerald-600 font-semibold'}>
                            {endDate.toLocaleDateString(locale)}
                            {isPast ? ' ⚠️' : ''}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {(user.last_active || user.last_login)
                        ? new Date(user.last_active || user.last_login).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })
                        : <span className="text-slate-400">{i18n.language === 'ar' ? 'لم يدخل' : 'Never'}</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 text-center">
                      <span className="font-semibold">{user.login_count || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditSubscription(user)}
                          className="hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4 me-1" />
                          {i18n.language === 'ar' ? 'تعديل' : i18n.language === 'sv' ? 'Redigera' : 'Edit'}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          disabled={sendingReminderUserId === user.id}
                          onClick={() => openReminderModal(user)}
                          className="hover:bg-emerald-50 text-emerald-700 hover:text-emerald-800 border-emerald-200"
                          title={i18n.language === 'ar' ? 'تعديل وإرسال إيميل تذكير' : 'Edit & send reminder email'}
                          data-testid={`send-expiry-reminder-btn-${user.id}`}
                        >
                          <Mail className="w-4 h-4 me-1" />
                          {sendingReminderUserId === user.id
                            ? (i18n.language === 'ar' ? 'جارٍ الإرسال...' : 'Sending...')
                            : (i18n.language === 'ar' ? 'تذكير' : i18n.language === 'sv' ? 'Påminn' : 'Remind')}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          disabled={sendingMessageUserId === user.id}
                          onClick={() => openMessageModal(user)}
                          className="hover:bg-sky-50 text-sky-700 hover:text-sky-800 border-sky-200"
                          title={i18n.language === 'ar' ? 'إرسال رسالة حرة' : 'Send custom message'}
                          data-testid={`send-message-btn-${user.id}`}
                        >
                          <MessageSquare className="w-4 h-4 me-1" />
                          {sendingMessageUserId === user.id
                            ? (i18n.language === 'ar' ? 'جارٍ الإرسال...' : 'Sending...')
                            : (i18n.language === 'ar' ? 'رسالة' : i18n.language === 'sv' ? 'Meddelande' : 'Message')}
                        </Button>
                        
                        {planType !== 'free' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setCancelModalOpen(true);
                              }}
                              className="hover:bg-red-50 text-red-600 hover:text-red-700"
                            >
                              <XCircle className="w-4 h-4 me-1" />
                              {i18n.language === 'ar' ? 'إلغاء' : i18n.language === 'sv' ? 'Avbryt' : 'Cancel'}
                            </Button>

                            {isWithinRefundWindow && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setRefundModalOpen(true);
                                }}
                                className="hover:bg-amber-50 text-amber-600 hover:text-amber-700"
                                title={i18n.language === 'ar' ? 'نافذة الاسترداد 14 يوماً سارية' : '14-day refund window active'}
                              >
                                <RefreshCw className="w-4 h-4 me-1" />
                                {i18n.language === 'ar' ? 'استرداد' : i18n.language === 'sv' ? 'Återbetalning' : 'Refund'}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredSubscribers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{i18n.language === 'ar' ? 'لا يوجد مشتركين' : 'No subscribers found'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Subscription Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-emerald-600" />
              {i18n.language === 'ar' ? 'تعديل الاشتراك' : 'Edit Subscription'}
            </DialogTitle>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-4 mt-4">
              {/* User Info Card */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-200 dark:bg-slate-600 rounded-lg">
                    <Users className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 dark:text-slate-100">{editingUser.name}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{editingUser.email}</div>
                  </div>
                </div>
              </div>

              {/* Current Account Details */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-3">
                  {i18n.language === 'ar' ? 'معلومات الحساب الحالية' : 'Current Account Info'}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {i18n.language === 'ar' ? 'الخطة الحالية:' : 'Current Plan:'}
                    </span>
                    <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5">
                      {getPlanName(editingUser.subscription_plan || 'free')}
                    </div>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {i18n.language === 'ar' ? 'الحالة:' : 'Status:'}
                    </span>
                    <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5">
                      {editingUser.subscription_status === 'active' ? (i18n.language === 'ar' ? 'نشط' : 'Active') 
                       : editingUser.subscription_status === 'expired' ? (i18n.language === 'ar' ? 'منتهي' : 'Expired')
                       : editingUser.subscription_status === 'trial' ? (i18n.language === 'ar' ? 'تجريبي' : 'Trial')
                       : (i18n.language === 'ar' ? 'مجاني' : 'Free')}
                    </div>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {i18n.language === 'ar' ? 'تاريخ التسجيل:' : 'Registered:'}
                    </span>
                    <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5">
                      {editingUser.created_at 
                        ? new Date(editingUser.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {i18n.language === 'ar' ? 'تاريخ الانتهاء:' : 'Expires:'}
                    </span>
                    <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5">
                      {editingUser.subscription_plan === 'lifetime' 
                        ? (i18n.language === 'ar' ? 'دائم' : 'Lifetime')
                        : editingUser.subscription_end 
                          ? new Date(editingUser.subscription_end).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')
                          : (i18n.language === 'ar' ? 'غير محدد' : 'N/A')}
                    </div>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {i18n.language === 'ar' ? 'عدد الدخول:' : 'Logins:'}
                    </span>
                    <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5">
                      {editingUser.login_count || 0}
                    </div>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {i18n.language === 'ar' ? 'آخر ظهور:' : 'Last seen:'}
                    </span>
                    <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5">
                      {(editingUser.last_active || editingUser.last_login)
                        ? new Date(editingUser.last_active || editingUser.last_login).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })
                        : '-'}
                    </div>
                  </div>
                  {editingUser.auth_provider && (
                    <div>
                      <span className="text-blue-600 dark:text-blue-400 font-medium">
                        {i18n.language === 'ar' ? 'طريقة التسجيل:' : 'Auth:'}
                      </span>
                      <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5">
                        {editingUser.auth_provider === 'google' ? 'Google' : editingUser.auth_provider}
                      </div>
                    </div>
                  )}
                  {editingUser.stripe_customer_id && (
                    <div>
                      <span className="text-blue-600 dark:text-blue-400 font-medium">Stripe ID:</span>
                      <div className="font-bold text-blue-900 dark:text-blue-200 mt-0.5 text-xs font-mono">
                        {editingUser.stripe_customer_id}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {i18n.language === 'ar' ? 'خطة الاشتراك' : i18n.language === 'sv' ? 'Prenumerationsplan' : 'Subscription Plan'}
                </Label>
                <Select 
                  value={editingUser.newSubscriptionPlan} 
                  onValueChange={(val) => setEditingUser({ ...editingUser, newSubscriptionPlan: val })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      // Build plan options from API plans with correct type mapping
                      const mapped = new Set();
                      const items = [];
                      
                      if (plans && plans.length > 0) {
                        plans.forEach((plan) => {
                          let planType = 'free';
                          let emoji = '👤';
                          
                          if (plan.price > 0) {
                            if (plan.durationType === 'months' && plan.duration === 1) {
                              planType = 'monthly';
                              emoji = '💎';
                            } else if (plan.durationType === 'months' && plan.duration === 6) {
                              planType = 'six_months';
                              emoji = '⭐';
                            } else if (plan.durationType === 'years' || (plan.durationType === 'months' && plan.duration === 12)) {
                              planType = 'yearly';
                              emoji = '🏆';
                            } else if (plan.durationType === 'lifetime' || plan.duration_type === 'lifetime') {
                              planType = 'lifetime';
                              emoji = '👑';
                            }
                          }
                          
                          if (!mapped.has(planType)) {
                            mapped.add(planType);
                            items.push(
                              <SelectItem key={plan.id} value={planType}>
                                <div className="flex items-center gap-2">
                                  <span>{emoji}</span>
                                  <span>{plan.name}</span>
                                  <span className="text-xs text-slate-500">
                                    ({plan.price} {plan.currency})
                                  </span>
                                </div>
                              </SelectItem>
                            );
                          }
                        });
                      }
                      
                      // Always ensure all plan types are available for admin
                      if (!mapped.has('free')) {
                        items.push(
                          <SelectItem key="fallback-free" value="free">
                            <div className="flex items-center gap-2">
                              <span>👤</span><span>{getPlanName('free')}</span>
                              <span className="text-xs text-slate-500">(0 kr)</span>
                            </div>
                          </SelectItem>
                        );
                      }
                      if (!mapped.has('monthly')) {
                        items.push(
                          <SelectItem key="fallback-monthly" value="monthly">
                            <div className="flex items-center gap-2">
                              <span>💎</span><span>{getPlanName('monthly')}</span>
                            </div>
                          </SelectItem>
                        );
                      }
                      if (!mapped.has('six_months')) {
                        items.push(
                          <SelectItem key="fallback-six_months" value="six_months">
                            <div className="flex items-center gap-2">
                              <span>⭐</span><span>{getPlanName('six_months')}</span>
                            </div>
                          </SelectItem>
                        );
                      }
                      if (!mapped.has('yearly')) {
                        items.push(
                          <SelectItem key="fallback-yearly" value="yearly">
                            <div className="flex items-center gap-2">
                              <span>🏆</span><span>{getPlanName('yearly')}</span>
                              <span className="text-xs text-orange-600">(12 {i18n.language === 'ar' ? 'شهر' : i18n.language === 'sv' ? 'mån' : 'months'})</span>
                            </div>
                          </SelectItem>
                        );
                      }
                      if (!mapped.has('lifetime')) {
                        items.push(
                          <SelectItem key="fallback-lifetime" value="lifetime">
                            <div className="flex items-center gap-2">
                              <span>👑</span><span>{getPlanName('lifetime')}</span>
                              <span className="text-xs text-amber-600">({i18n.language === 'ar' ? 'لا ينتهي' : i18n.language === 'sv' ? 'Aldrig utgår' : 'Never expires'})</span>
                            </div>
                          </SelectItem>
                        );
                      }
                      // Always offer the 30 SEK / 1-hour trial as a manual
                      // admin option so the operator can gift or fix a pass
                      // for a specific user in one click.
                      items.push(
                        <SelectItem key="option-trial-hour" value="trial_hour">
                          <div className="flex items-center gap-2">
                            <span>⏱️</span><span>{getPlanName('trial_hour')}</span>
                            <span className="text-xs text-emerald-600">
                              (29 SEK · 60 {i18n.language === 'ar' ? 'دقيقة' : i18n.language === 'sv' ? 'min' : 'min'})
                            </span>
                          </div>
                        </SelectItem>
                      );
                      
                      return items;
                    })()}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {i18n.language === 'ar' ? 'اختر خطة الاشتراك للمستخدم' : i18n.language === 'sv' ? 'Välj prenumerationsplan för användaren' : 'Select subscription plan for user'}
                </p>
              </div>

              {editingUser.newSubscriptionPlan !== 'free' && editingUser.newSubscriptionPlan !== 'lifetime' && (
                <>
                  <div>
                    <Label>{i18n.language === 'ar' ? 'حالة الاشتراك' : i18n.language === 'sv' ? 'Prenumerationsstatus' : 'Subscription Status'}</Label>
                    <Select 
                      value={editingUser.newSubscriptionStatus} 
                      onValueChange={(val) => setEditingUser({ ...editingUser, newSubscriptionStatus: val })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">
                          {i18n.language === 'ar' ? '✅ نشط' : i18n.language === 'sv' ? '✅ Aktiv' : '✅ Active'}
                        </SelectItem>
                        <SelectItem value="expired">
                          {i18n.language === 'ar' ? '⏰ منتهي' : i18n.language === 'sv' ? '⏰ Utgånget' : '⏰ Expired'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{i18n.language === 'ar' ? 'تاريخ انتهاء الاشتراك' : i18n.language === 'sv' ? 'Prenumerationens slutdatum' : 'Subscription End Date'}</Label>
                    <Input
                      type="date"
                      value={editingUser.newSubscriptionEnd || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, newSubscriptionEnd: e.target.value })}
                      className="mt-2"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {i18n.language === 'ar' ? 'اتركه فارغاً ليتم حسابه تلقائياً' : i18n.language === 'sv' ? 'Lämna tomt för automatisk beräkning' : 'Leave empty for automatic calculation'}
                    </p>
                  </div>
                </>
              )}
              
              {editingUser.newSubscriptionPlan === 'lifetime' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <span className="text-2xl">👑</span>
                    <div>
                      <p className="font-semibold">{i18n.language === 'ar' ? 'اشتراك دائم' : i18n.language === 'sv' ? 'Livstidsprenumeration' : 'Lifetime Subscription'}</p>
                      <p className="text-sm">{i18n.language === 'ar' ? 'هذا الاشتراك لا ينتهي أبداً' : i18n.language === 'sv' ? 'Denna prenumeration löper aldrig ut' : 'This subscription never expires'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : i18n.language === 'sv' ? 'Avbryt' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleUpdateSubscription}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            >
              <Save className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'حفظ التغييرات' : i18n.language === 'sv' ? 'Spara ändringar' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-6 h-6" />
              {i18n.language === 'ar' ? 'إلغاء الاشتراك' : i18n.language === 'sv' ? 'Avbryt prenumeration' : 'Cancel Subscription'}
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-900 mb-1">
                      {i18n.language === 'ar' ? 'تحذير!' : i18n.language === 'sv' ? 'Varning!' : 'Warning!'}
                    </p>
                    <p className="text-sm text-red-700">
                      {i18n.language === 'ar' 
                        ? 'سيتم إلغاء اشتراك هذا المستخدم وتحويله إلى الخطة المجانية فوراً.' 
                        : i18n.language === 'sv'
                        ? 'Denna användares prenumeration kommer att avbrytas och konverteras till gratisplan omedelbart.'
                        : 'This user\'s subscription will be cancelled and converted to free plan immediately.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <div className="font-semibold text-slate-900 mb-1">{selectedUser.name}</div>
                <div className="text-sm text-slate-600">{selectedUser.email}</div>
                <div className="mt-2 text-sm">
                  <span className="text-slate-500">{i18n.language === 'ar' ? 'الخطة الحالية:' : i18n.language === 'sv' ? 'Nuvarande plan:' : 'Current Plan:'}</span>
                  <span className="font-semibold text-slate-900 ms-2">{getPlanName(selectedUser.subscription_plan)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              {i18n.language === 'ar' ? 'رجوع' : i18n.language === 'sv' ? 'Tillbaka' : 'Go Back'}
            </Button>
            <Button 
              onClick={handleCancelSubscription}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <XCircle className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'تأكيد الإلغاء' : i18n.language === 'sv' ? 'Bekräfta avbokning' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={refundModalOpen} onOpenChange={setRefundModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCw className="w-6 h-6" />
              {i18n.language === 'ar' ? 'استرداد الأموال' : i18n.language === 'sv' ? 'Återbetalning' : 'Process Refund'}
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-green-900 mb-1">
                      {i18n.language === 'ar' ? 'استرداد تلقائي عبر Stripe' : i18n.language === 'sv' ? 'Automatisk återbetalning via Stripe' : 'Automatic Refund via Stripe'}
                    </p>
                    <p className="text-sm text-green-700">
                      {i18n.language === 'ar' 
                        ? 'سيتم إلغاء الاشتراك وإرجاع المبلغ تلقائياً إلى بطاقة العميل عبر Stripe.' 
                        : i18n.language === 'sv'
                        ? 'Prenumerationen kommer att avbrytas och beloppet återbetalas automatiskt till kundens kort via Stripe.'
                        : 'The subscription will be cancelled and the amount will be automatically refunded to the customer\'s card via Stripe.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <div className="font-semibold text-slate-900 mb-1">{selectedUser.name}</div>
                <div className="text-sm text-slate-600">{selectedUser.email}</div>
                <div className="mt-2 text-sm">
                  <span className="text-slate-500">{i18n.language === 'ar' ? 'الخطة:' : i18n.language === 'sv' ? 'Plan:' : 'Plan:'}</span>
                  <span className="font-semibold text-slate-900 ms-2">{getPlanName(selectedUser.subscription_plan)}</span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  ⚠️ {i18n.language === 'ar' 
                    ? 'هذا الإجراء لا يمكن التراجع عنه. سيتم إرجاع المبلغ الكامل للعميل.' 
                    : i18n.language === 'sv'
                    ? 'Denna åtgärd kan inte ångras. Hela beloppet återbetalas till kunden.'
                    : 'This action cannot be undone. The full amount will be refunded to the customer.'}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setRefundModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : i18n.language === 'sv' ? 'Avbryt' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleRefund}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <RefreshCw className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'تأكيد الاسترداد' : i18n.language === 'sv' ? 'Bekräfta återbetalning' : 'Confirm Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Edit & Send Modal */}
      <Dialog open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              {i18n.language === 'ar'
                ? 'تعديل وإرسال إيميل التذكير'
                : i18n.language === 'sv' ? 'Redigera och skicka påminnelse' : 'Edit & Send Reminder Email'}
            </DialogTitle>
            {reminderUser && (
              <div className="text-sm text-slate-500 mt-1">
                {i18n.language === 'ar' ? 'إلى:' : 'To:'} <strong>{reminderUser.email}</strong>
                {' · '}
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700">
                  {reminderFields.language?.toUpperCase()} · {reminderFields.days_remaining} {i18n.language === 'ar' ? 'يوم متبقي' : 'days left'} · {reminderFields.expiry_date}
                </span>
              </div>
            )}
          </DialogHeader>

          {loadingTemplate ? (
            <div className="py-12 text-center text-slate-500">
              {i18n.language === 'ar' ? 'جارٍ تحميل القالب...' : 'Loading template...'}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Editable fields column */}
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'لغة الإيميل' : i18n.language === 'sv' ? 'E-postspråk' : 'Email language'}
                  </Label>
                  <select
                    value={reminderFields.language}
                    onChange={(e) => handleReminderLanguageChange(e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="reminder-language-select"
                  >
                    <option value="sv">🇸🇪 Svenska (default)</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="ar">🇸🇦 العربية</option>
                    <option value="tr">🇹🇷 Türkçe</option>
                    <option value="fa">🇮🇷 فارسی</option>
                    <option value="so">🇸🇴 Soomaali</option>
                    <option value="pl">🇵🇱 Polski</option>
                    <option value="es">🇪🇸 Español</option>
                    <option value="de">🇩🇪 Deutsch</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'عنوان الإيميل' : i18n.language === 'sv' ? 'Ämne' : 'Subject'}
                  </Label>
                  <Input
                    value={reminderFields.subject}
                    onChange={(e) => setReminderFields({ ...reminderFields, subject: e.target.value })}
                    data-testid="reminder-subject-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'العنوان الرئيسي' : 'Headline'}
                  </Label>
                  <Input
                    value={reminderFields.headline}
                    onChange={(e) => setReminderFields({ ...reminderFields, headline: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'تحية' : 'Greeting'}
                  </Label>
                  <Input
                    value={reminderFields.hello}
                    onChange={(e) => setReminderFields({ ...reminderFields, hello: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'النص التعريفي' : 'Intro / Body'}
                  </Label>
                  <textarea
                    value={reminderFields.intro}
                    onChange={(e) => setReminderFields({ ...reminderFields, intro: e.target.value })}
                    rows={4}
                    className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="reminder-intro-textarea"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'عنوان قائمة المزايا' : '"What you’ll lose" label'}
                  </Label>
                  <Input
                    value={reminderFields.what_you_lose}
                    onChange={(e) => setReminderFields({ ...reminderFields, what_you_lose: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'قائمة المزايا (4 أسطر)' : 'Features list (4 lines)'}
                  </Label>
                  <textarea
                    value={(reminderFields.features || []).join('\n')}
                    onChange={(e) => {
                      const lines = e.target.value.split('\n').slice(0, 4);
                      while (lines.length < 4) lines.push('');
                      setReminderFields({ ...reminderFields, features: lines });
                    }}
                    rows={4}
                    className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder={'✅ Feature 1\n✅ Feature 2\n✅ Feature 3\n✅ Feature 4'}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">
                      {i18n.language === 'ar' ? 'نص زر CTA' : 'CTA button text'}
                    </Label>
                    <Input
                      value={reminderFields.cta}
                      onChange={(e) => setReminderFields({ ...reminderFields, cta: e.target.value })}
                      data-testid="reminder-cta-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">
                      {i18n.language === 'ar' ? 'أيام متبقية' : 'Days left'}
                    </Label>
                    <Input
                      type="number"
                      value={reminderFields.days_remaining}
                      onChange={(e) => setReminderFields({ ...reminderFields, days_remaining: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'تذييل' : 'Footer'}
                  </Label>
                  <Input
                    value={reminderFields.footer}
                    onChange={(e) => setReminderFields({ ...reminderFields, footer: e.target.value })}
                  />
                </div>
              </div>

              {/* Live preview column */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'المعاينة الحية' : 'Live preview'}
                  </Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePreviewReminder}
                    disabled={previewLoading}
                    data-testid="reminder-preview-btn"
                  >
                    {previewLoading
                      ? (i18n.language === 'ar' ? 'جارٍ التحديث...' : 'Loading...')
                      : (i18n.language === 'ar' ? '🔄 تحديث المعاينة' : '🔄 Refresh preview')}
                  </Button>
                </div>
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900 min-h-[500px]">
                  {previewHtml ? (
                    <iframe
                      title="Email preview"
                      srcDoc={previewHtml}
                      className="w-full"
                      style={{ height: '600px', border: 'none' }}
                    />
                  ) : (
                    <div className="p-8 text-center text-sm text-slate-400">
                      {i18n.language === 'ar'
                        ? 'اضغط "تحديث المعاينة" لرؤية الإيميل بشكله النهائي'
                        : 'Click "Refresh preview" to see the final rendered email'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReminderModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSendCustomReminder}
              disabled={loadingTemplate || sendingReminderUserId === reminderUser?.id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="reminder-send-btn"
            >
              <Mail className="w-4 h-4 me-2" />
              {sendingReminderUserId === reminderUser?.id
                ? (i18n.language === 'ar' ? 'جارٍ الإرسال...' : 'Sending...')
                : (i18n.language === 'ar' ? 'إرسال الإيميل الآن' : 'Send Email Now')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Free-form Custom Message Modal */}
      <Dialog open={messageModalOpen} onOpenChange={setMessageModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-sky-600" />
              {i18n.language === 'ar' ? 'إرسال رسالة حرة' : i18n.language === 'sv' ? 'Skicka fritt meddelande' : 'Send Custom Message'}
            </DialogTitle>
            {messageUser && (
              <div className="text-sm text-slate-500 mt-1">
                {i18n.language === 'ar' ? 'إلى:' : 'To:'} <strong>{messageUser.email}</strong>
              </div>
            )}
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">
                  {i18n.language === 'ar' ? 'لغة الإيميل' : 'Email language'}
                </Label>
                <select
                  value={messageFields.language}
                  onChange={(e) => {
                    setMessageFields({ ...messageFields, language: e.target.value });
                    setMessagePreviewHtml('');
                  }}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="message-language-select"
                >
                  <option value="sv">🇸🇪 Svenska (default)</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="ar">🇸🇦 العربية</option>
                  <option value="tr">🇹🇷 Türkçe</option>
                  <option value="fa">🇮🇷 فارسی</option>
                  <option value="so">🇸🇴 Soomaali</option>
                  <option value="pl">🇵🇱 Polski</option>
                  <option value="es">🇪🇸 Español</option>
                  <option value="de">🇩🇪 Deutsch</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">
                  {i18n.language === 'ar' ? 'عنوان الإيميل' : 'Subject'}
                </Label>
                <Input
                  value={messageFields.subject}
                  onChange={(e) => setMessageFields({ ...messageFields, subject: e.target.value })}
                  placeholder={i18n.language === 'ar' ? 'مثال: تحديث مهم من DriveMaster' : 'e.g. Important update from DriveMaster'}
                  data-testid="message-subject-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">
                  {i18n.language === 'ar' ? 'نص الرسالة' : 'Message body'}
                </Label>
                <textarea
                  value={messageFields.body}
                  onChange={(e) => setMessageFields({ ...messageFields, body: e.target.value })}
                  rows={10}
                  placeholder={i18n.language === 'ar' ? 'اكتب رسالتك هنا...\n\nيمكنك استخدام أسطر متعددة.' : 'Write your message here...\n\nMulti-line supported.'}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="message-body-textarea"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'نص الزر (اختياري)' : 'Button text (optional)'}
                  </Label>
                  <Input
                    value={messageFields.cta_text}
                    onChange={(e) => setMessageFields({ ...messageFields, cta_text: e.target.value })}
                    placeholder={i18n.language === 'ar' ? 'مثل: اذهب الآن' : 'e.g. Visit now'}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">
                    {i18n.language === 'ar' ? 'رابط الزر' : 'Button URL'}
                  </Label>
                  <Input
                    value={messageFields.cta_url}
                    onChange={(e) => setMessageFields({ ...messageFields, cta_url: e.target.value })}
                    placeholder="https://drivemaster.se/..."
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase text-slate-500">
                  {i18n.language === 'ar' ? 'المعاينة الحية' : 'Live preview'}
                </Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePreviewMessage}
                  disabled={messagePreviewLoading || !messageFields.subject || !messageFields.body}
                  data-testid="message-preview-btn"
                >
                  {messagePreviewLoading
                    ? (i18n.language === 'ar' ? 'جارٍ التحديث...' : 'Loading...')
                    : (i18n.language === 'ar' ? '🔄 تحديث المعاينة' : '🔄 Refresh preview')}
                </Button>
              </div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900 min-h-[500px]">
                {messagePreviewHtml ? (
                  <iframe
                    title="Message preview"
                    srcDoc={messagePreviewHtml}
                    className="w-full"
                    style={{ height: '600px', border: 'none' }}
                  />
                ) : (
                  <div className="p-8 text-center text-sm text-slate-400">
                    {i18n.language === 'ar'
                      ? 'املأ العنوان والنص ثم اضغط "تحديث المعاينة"'
                      : 'Fill in subject and body, then click "Refresh preview"'}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMessageModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSendCustomMessage}
              disabled={sendingMessageUserId === messageUser?.id || !messageFields.subject || !messageFields.body}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="message-send-btn"
            >
              <MessageSquare className="w-4 h-4 me-2" />
              {sendingMessageUserId === messageUser?.id
                ? (i18n.language === 'ar' ? 'جارٍ الإرسال...' : 'Sending...')
                : (i18n.language === 'ar' ? 'إرسال الرسالة' : 'Send Message')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionManagement;